import { Flame, Tent, Users, Shield, Swords, PartyPopper } from 'lucide-react'
import { cn } from '../../lib/cn'
import { libraryBadge } from '../../lib/format'
import type { GroupMember, TogethernessMode } from '../../lib/types'
import { useSessionBuilder } from '../../contexts/SessionBuilderContext'
import { Avatar, Badge, Button, Chip, EmptyState } from '../ui'

const TOGETHERNESS_GLYPHS: Record<TogethernessMode, { label: string; hint: string; icon: React.ReactNode }> = {
  single_player: { label: 'Solo', hint: 'Each on their own', icon: <Users className="h-4 w-4" /> },
  joinable_parallel: { label: 'Joinable', hint: 'Alone together', icon: <Users className="h-4 w-4" /> },
  true_coop: { label: 'Co-op', hint: 'Truly together', icon: <Shield className="h-4 w-4" /> },
  competitive: { label: 'Competitive', hint: 'Against each other', icon: <Swords className="h-4 w-4" /> },
  party: { label: 'Party', hint: 'Loud and silly', icon: <PartyPopper className="h-4 w-4" /> },
}
const TOGETHERNESS_ORDER: TogethernessMode[] = [
  'single_player',
  'joinable_parallel',
  'true_coop',
  'competitive',
  'party',
]

interface RosterStepProps {
  members: GroupMember[]
  onNext: () => void
}

export function RosterStep({ members, onNext }: RosterStepProps) {
  const { state, dispatch } = useSessionBuilder()
  const n = state.rosterUserIds.length

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h2 className="font-display text-3xl leading-tight text-text-strong">Who&apos;s around the fire?</h2>
        <p className="text-sm text-text-muted">Tap everyone who&apos;s in tonight — any mix, even just you.</p>
      </header>

      {members.length === 0 ? (
        <EmptyState
          icon={<Tent className="h-10 w-10" strokeWidth={1.5} />}
          title="Who's around the fire tonight?"
          description="Add friends to this group first — their Steam libraries light up the recommendations."
        />
      ) : (
        <fieldset className="space-y-3">
          <legend className="sr-only">Choose tonight&apos;s roster</legend>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {members.map((m) => {
              const selected = state.rosterUserIds.includes(m.user_id)
              const badge = libraryBadge(m.library_status, m.owned_game_count)
              return (
                <label
                  key={m.user_id}
                  className={cn(
                    'relative flex cursor-pointer flex-col items-center gap-2 rounded-card p-4 text-center transition duration-150 ease-out',
                    selected
                      ? 'bg-ember-500/10 ring-2 ring-ember-400/70 shadow-glow'
                      : 'bg-surface ring-1 ring-border hover:ring-border-warm',
                  )}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={selected}
                    aria-checked={selected}
                    onChange={() => dispatch({ type: 'toggleMember', userId: m.user_id })}
                  />
                  {selected && (
                    <span
                      className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-ember-400 text-text-on-ember"
                      aria-hidden="true"
                    >
                      <Flame className="h-3 w-3" strokeWidth={2.25} />
                    </span>
                  )}
                  <Avatar name={m.display_name} src={m.avatar_url} size="lg" />
                  <span className={cn('text-sm font-medium', selected ? 'text-text-strong' : 'text-text')}>
                    {m.display_name ?? 'Steam friend'}
                  </span>
                  {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
                </label>
              )
            })}
          </div>
        </fieldset>
      )}

      {n > 0 && (
        <>
          {/* Togetherness: full spectrum, single-select, per-session weight/gate.
              A labeled group of aria-pressed toggles (the VibeStep chip pattern,
              single-select enforced by the reducer) — NOT role="radiogroup",
              whose APG contract (roving tabindex + arrow keys) plain chips
              don't honor. */}
          <section className="space-y-2" role="group" aria-label="Togetherness mode">
            <h3 className="font-display text-xl font-semibold text-text-strong">How together?</h3>
            <div className="flex flex-wrap gap-2 pt-1">
              {TOGETHERNESS_ORDER.map((mode) => {
                const g = TOGETHERNESS_GLYPHS[mode]
                return (
                  <Chip
                    key={mode}
                    selected={state.togethernessMode === mode}
                    onToggle={() => dispatch({ type: 'setTogethernessMode', mode })}
                    icon={g.icon}
                  >
                    {g.label}
                  </Chip>
                )
              })}
            </div>
            {/* muted: the hint explains the chosen mode — real content (1.4.3). */}
            <p className="text-xs text-text-muted">
              {TOGETHERNESS_GLYPHS[state.togethernessMode].hint}
            </p>
          </section>
        </>
      )}

      {/* flex-wrap: at tight widths the CTA drops to its own row whole — labels
          never wrap mid-word (Button is nowrap) and never clip. */}
      <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
        <span className="inline-flex items-center gap-2 text-sm text-text-muted">
          <Flame className="h-4 w-4 text-ember-400" strokeWidth={1.75} aria-hidden="true" />
          {n === 0 ? 'Nobody yet' : n === 1 ? '1 around the fire' : `${n} around the fire`}
        </span>
        <Button onClick={onNext} disabled={n === 0}>
          Next: the vibe
        </Button>
      </footer>
    </div>
  )
}
